import React, { useMemo, useState, useRef, useEffect } from 'react';
import InputMask from 'react-input-mask';
import upperFirst from 'lodash/upperFirst';
import cn from 'classnames';
import { Icon, Tooltip } from '@landing/ui';

import { useClickOutside } from 'utils/hooks';

import * as styles from './Input.module.scss';

export const Input = ({
  field,
  theme,
  name,
  disabled,
  onChange,
  onPasteCapture,
  mask,
  prependedTooltip,
  tooltip,
  isTextarea,
  clearValue,
  upload,
  isTooltipVisible,
  errorsShakeHub,
  isErrorActive,
  domProps,
  className,
  regex,
  minRows,
  maxRows,
  focused,
  setFocused,
}) => {
  const [isOpenPrependedTooltop, setIsOpenPrependedTooltop] = useState(false);
  const inputRef = useRef();
  const prependedTooltipRef = useRef();
  const closeButtonRef = useRef();

  const InputTag = useMemo(() => {
    if (isTextarea) return 'textarea';
    if (mask) return InputMask;

    return 'input';
  }, [isTextarea]);

  useEffect(() => {
    errorsShakeHub.addEventListener('setFocus', onSetFocusHandler);

    return () => {
      errorsShakeHub.removeEventListener('setFocus', onSetFocusHandler);
    };
  }, [errorsShakeHub]);

  useEffect(() => {
    if (isTextarea && inputRef.current) {
      const el = inputRef.current;
      autoGrow(el);
    }
  }, [isTextarea, inputRef.current, inputRef.current?.value]);

  useClickOutside([inputRef], () => setFocused(false));
  useClickOutside([prependedTooltipRef], () => setIsOpenPrependedTooltop(false));

  const inputChangeHandler = e => {
    if (upload) {
      onChange(e);
    } else {
      onChange(e.target.value);
    }
  };

  const autoGrow = el => {
    const { lineHeight, maxHeight } = window.getComputedStyle(el);

    if (el.scrollHeight > parseFloat(maxHeight)) {
      el.classList.add(styles.scrollbarShow);
    } else {
      el.classList.remove(styles.scrollbarShow);
    }

    el.style.height = '0';
    el.style.height =
      el.scrollHeight > parseFloat(lineHeight) ? `${el.scrollHeight}px` : lineHeight;
  };

  const style = {
    '--maxRows': maxRows,
    '--minRows': minRows,
  };

  const closeButtonMouseDownHandler = e => {
    onChange('');
    setFocused(false);
    e.stopPropagation();
  };

  const inputMouseDownHandler = () => {
    setFocused(true);
  };

  const inputValueOnKeydown = e => {
    if (regex && e.key.length === 1 && regex.test(e.key)) {
      e.preventDefault();
    }
  };

  const onSetFocusHandler = ({ detail }) => {
    if (detail === name) {
      inputRef.current.focus();
      setFocused(true);
    }
  };
  return (
    <>
      <label
        className={cn(
          styles.label,
          className,
          theme && styles[`theme${upperFirst(theme)}`],
          isTextarea && styles.textAreaWrapper,
        )}
      >
        {prependedTooltip && (
          <div
            className={styles.prependedTooltip}
            onClick={() => setIsOpenPrependedTooltop(true)}
            ref={prependedTooltipRef}
          >
            <Icon name="union" className={styles.prependedIcon} />
            {isOpenPrependedTooltop && (
              <Tooltip className={styles.tooltip} text={prependedTooltip} />
            )}
          </div>
        )}
        <InputTag
          ref={inputRef}
          className={cn(
            isErrorActive && styles.error,
            prependedTooltip && styles.withPrependedTooltip,
          )}
          style={style}
          name={name}
          disabled={disabled}
          onInput={inputChangeHandler}
          onMouseDown={inputMouseDownHandler}
          onKeyDown={inputValueOnKeydown}
          onPasteCapture={onPasteCapture}
          {...mask}
          {...field}
          {...domProps}
        />
        {domProps.placeholder && (
          <span className={cn(styles.placeholder, isTextarea && 'textareaPlaceholder')}>
            {domProps.placeholder}
          </span>
        )}
        {clearValue && focused && (
          <div
            ref={closeButtonRef}
            className={styles.closeButton}
            onMouseDown={closeButtonMouseDownHandler}
          />
        )}
        {isTooltipVisible && focused && <Tooltip className={styles.tooltip} text={tooltip} />}
      </label>
    </>
  );
};
